import { Injectable, inject, Injector } from '@angular/core';
import { LatLng } from 'leaflet';
import { WmsFieldMappingHandler, TransformationService } from '@luzula/luzula-lib';
import { AufnahmePunktDocument, istLeer } from '../../data-model';
import type { DataService } from '../../data.service';

/**
 * Handler für das WMS-Feld-Mapping bei AufnahmePunktDocument.
 * Ermöglicht dem WmsDataService, Dokumente zu befüllen ohne das Datenmodell zu kennen.
 */
@Injectable({ providedIn: 'root' })
export class BohrpunktWmsFieldMappingHandler implements WmsFieldMappingHandler<AufnahmePunktDocument> {
	private ts = inject(TransformationService);
	private injector = inject(Injector);

	/**
	 * Liefert die Koordinaten für WMS-Abfragen.
	 */
	getCoordinates(bp: AufnahmePunktDocument): LatLng {
		return this.ts.from(bp.header.koordinaten);
	}

	/**
	 * Prüft ob Klimastufe abgefragt werden soll (wenn neu-Feld leer).
	 */
	shouldFetchKlimastufe(bp: AufnahmePunktDocument): boolean {
		return istLeer(bp.extendedHeader?.klimaStufe?.neu);
	}

	/**
	 * Setzt die Klimastufe-Daten.
	 */
	setKlimastufeData(bp: AufnahmePunktDocument, data: { klimastufe?: string }): void {
		if (!bp.extendedHeader) return;
		if (!bp.extendedHeader.klimaStufe) {
			bp.extendedHeader.klimaStufe = { neu: '' };
		}
		bp.extendedHeader.klimaStufe.neu = data.klimastufe as any || '';
	}

	/**
	 * Prüft ob Forstadresse abgefragt werden soll (wenn Abteilung leer).
	 */
	shouldFetchForstadresse(bp: AufnahmePunktDocument): boolean {
		return istLeer(bp.header?.adresse?.abteilung);
	}

	/**
	 * Setzt die Forstadresse-Daten.
	 */
	setForstadresseData(bp: AufnahmePunktDocument, data: {
		abt?: string;
		ua?: string;
		tf?: string;
		fa_txt?: string;
		fr_txt?: string;
		schl_gis?: string;
	}): void {
		if (!bp.header?.adresse) return;
		bp.header.adresse.abteilung = data.abt || '';
		bp.header.adresse.unterabteilung = data.ua || '';
		// Teilfläche aus schl_gis extrahieren (Format: "FA_FR_ABT_UA_?_UA_TF_?")
		// Beispiel: "13_1_8_8_1405_x_10_1" -> UA=x, TF=10
		// UA kann ein beliebiger Buchstabe sein (a-z)
		if (data.schl_gis) {
			const match = data.schl_gis.match(/_[a-z]_(\d+)_/i);
			if (match) {
				bp.header.adresse.teilflaeche = match[1];
			}
		}
		// Forstamt/Revier mit Nummern setzen (z.B. "06 Neustrelitz", "04 Theerofen")
		bp.header.forstamt = this.withNr(data.fa_txt, data.fr_txt, 'FORSTAMT') || data.fa_txt || '';
		bp.header.revier = this.withNr(data.fa_txt, data.fr_txt, 'FORSTREVIE') || data.fr_txt || '';
	}

	/**
	 * Prüft ob Katasteradresse abgefragt werden soll (wenn Gemarkung leer).
	 */
	shouldFetchKatasteradresse(bp: AufnahmePunktDocument): boolean {
		return istLeer(bp.header?.adresse?.gemarkung);
	}

	/**
	 * Setzt die Katasteradresse-Daten.
	 */
	setKatasteradresseData(bp: AufnahmePunktDocument, data: {
		gmkname?: string;
		flurnr?: string;
		'cp:label'?: string;
	}): void {
		if (!bp.header?.adresse) return;
		bp.header.adresse.gemarkung = data.gmkname || '';
		// Flur aus flurnr: letzte 3 Ziffern als int (z.B. "134162002" -> 2)
		if (data.flurnr && data.flurnr.length >= 3) {
			const flurNum = parseInt(data.flurnr.slice(-3), 10);
			if (!isNaN(flurNum)) {
				bp.header.adresse.flur = flurNum;
			}
		}
		// Flurstück aus cp:label
		if (data['cp:label']) {
			bp.header.adresse.flurstueck = data['cp:label'];
		}
	}

	/**
	 * Prüft ob Wuchsgebiet abgefragt werden soll (wenn Wuchsgebiet leer).
	 */
	shouldFetchWuchsgebiet(bp: AufnahmePunktDocument): boolean {
		return istLeer(bp.extendedHeader?.wuchsgebiet);
	}

	/**
	 * Setzt die Wuchsgebiet-Daten.
	 */
	setWuchsgebietData(bp: AufnahmePunktDocument, data: { bez_wg_bu?: string; bez_wb_bu?: string }): void {
		if (!bp.extendedHeader) return;
		bp.extendedHeader.wuchsgebiet = data.bez_wg_bu || '';
		bp.extendedHeader.wuchsbezirk = data.bez_wb_bu || '';
	}

	/** Schlägt Nummer+Name aus Reviere-Tabelle nach (z.B. "06 Neustrelitz") */
	private withNr(faTxt: string | undefined, frTxt: string | undefined, col: 'FORSTAMT' | 'FORSTREVIE'): string {
		if (!faTxt || !frTxt) return '';
		try {
			const { DataService } = require('../../data.service');
			const ds = this.injector.get(DataService) as DataService;
			const reviere = ds.getHardcoded('Reviere') as any[];
			if (!reviere) return '';
			const entry = reviere.find((r: any) => r.FA_TXT === faTxt && r.FR_TXT === frTxt);
			return entry ? entry[col] : '';
		} catch { return ''; }
	}

	/**
	 * Speichert das Dokument nach WMS-Datenanreicherung in die DB.
	 * Nur für bereits gespeicherte Punkte (mit _id und _rev).
	 */
	saveDocument(bp: AufnahmePunktDocument): void {
		if (!(bp as any)._id || !(bp as any)._rev) return;
		// Lazy resolve um zirkuläre Abhängigkeit DataService → Handler → DataService zu vermeiden
		import('../../data.service').then(m => {
			const ds = this.injector.get(m.DataService) as DataService;
			ds.saveDocumentQuiet(bp).catch((err: any) =>
				console.warn('WMS Auto-Save fehlgeschlagen:', err)
			);
		});
	}
}
